import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  FEED_TYPES,
  FeedFilter,
  FeedType,
  feedTypeMeta,
} from "./tokens";

interface MetricTilesProps {
  counts: Record<FeedType, number>;
  subs: Record<FeedType, string>;
  filter: FeedFilter;
  onSelect: (type: FeedType) => void;
  // Lanes that have a full standalone page. The tile body keeps its existing
  // job (toggle the feed filter); the extra "View all" link is what navigates,
  // so the tile→chip filtering behaviour is untouched.
  viewAllHrefs?: Partial<Record<FeedType, string>>;
  // Optional replacement for the big number of a tile (the Active / Inactive
  // card shows two figures). The tile still filters the feed on `counts`.
  displays?: Partial<Record<FeedType, React.ReactNode>>;
}

const MetricTiles: React.FC<MetricTilesProps> = ({
  counts,
  subs,
  filter,
  onSelect,
  viewAllHrefs,
  displays,
}) => {
  const navigate = useNavigate();
  return (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))",
        sm: "repeat(3, minmax(0, 1fr))",
        lg: "repeat(5, minmax(0, 1fr))",
      },
      gap: "12px",
    }}
  >
    {FEED_TYPES.map((type) => {
      const meta = feedTypeMeta[type];
      const Icon = meta.icon;
      const active = filter === type;
      return (
        <Box
          key={type}
          role="button"
          aria-pressed={active}
          onClick={() => onSelect(type)}
          sx={{
            backgroundColor: active ? meta.tint : "#ffffff",
            border: `1.5px solid ${active ? meta.accent : "#ececee"}`,
            borderRadius: "14px",
            padding: "14px 16px",
            cursor: "pointer",
            transition: "transform 120ms ease, box-shadow 120ms ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 1px 5px rgba(0,0,0,0.08)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <Icon sx={{ fontSize: 18, color: meta.accent }} />
            <Typography
              sx={{
                fontSize: "12.5px",
                fontWeight: 700,
                color: "#55555c",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                lineHeight: 1.2,
              }}
            >
              {meta.tileLabel}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "30px",
              fontWeight: 800,
              color: meta.accent,
              lineHeight: 1.1,
              marginTop: "8px",
            }}
          >
            {displays?.[type] ?? counts[type]}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#7a7a80" }}>
            {subs[type]}
          </Typography>
          {viewAllHrefs?.[type] ? (
            <Typography
              role="link"
              onClick={(e) => {
                e.stopPropagation();
                navigate(viewAllHrefs[type] as string);
              }}
              sx={{
                marginTop: "6px",
                fontSize: "12px",
                fontWeight: 700,
                color: meta.accent,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View all →
            </Typography>
          ) : null}
        </Box>
      );
    })}
    </Box>
  );
};

export default React.memo(MetricTiles);
